import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, Pencil } from "lucide-react";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import AccountLayout from "./AccountLayout";

const statusColors: Record<string, string> = {
  pending: "bg-warning/10 text-warning",
  approved: "bg-success/10 text-success",
  rejected: "bg-destructive/10 text-destructive",
};
const statusLabels: Record<string, string> = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
};

export default function AccountReviews() {
  useSeoMeta({ pageType: "user_reviews" });
  const { user } = useAuth();

  const { data: myReviews, isLoading } = useQuery({
    queryKey: ["my-reviews-list", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("reviews")
        .select("*, venues(name, slug)")
        .eq("user_id", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  return (
    <AccountLayout>
      {isLoading ? (
        <Skeleton className="h-32" />
      ) : !myReviews?.length ? (
        <p className="text-sm text-muted-foreground">
          Aún no has enviado ninguna review.{" "}
          <Link to="/" className="text-primary underline">Busca una sala</Link> para contribuir.
        </p>
      ) : (
        <div className="space-y-3">
          {myReviews.map((review) => {
            const venue = review.venues as any;
            return (
              <Card key={review.id} className="transition-colors hover:bg-muted/30">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/mi-cuenta/reviews/${review.id}`}
                      className="font-medium hover:text-primary"
                    >
                      {venue?.name ?? "Sala"}
                    </Link>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-0.5">
                        <Star className="h-3 w-3 fill-warning text-warning" />
                        {Number(review.overall_rating).toFixed(1)}
                      </span>
                      {review.event_date && (
                        <>
                          <span>·</span>
                          <span>Evento: {new Date(review.event_date).toLocaleDateString("es-ES")}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>Enviada: {new Date(review.created_at).toLocaleDateString("es-ES")}</span>
                      {(review as any).is_edited && (
                        <>
                          <span>·</span>
                          <span className="flex items-center gap-0.5">
                            <Pencil className="h-3 w-3" /> Editada
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusColors[review.status ?? "pending"]}>
                      {statusLabels[review.status ?? "pending"]}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </AccountLayout>
  );
}
